/**
 * Network Domain Ontology Registration
 *
 * Registers semantic types specific to the network/infrastructure domain.
 * These extend the abstract hierarchy in `src/lib/graph/ontology.ts` so
 * cross-domain queries (Compass, AI tools) work via `isA()`.
 *
 * The semantic types here are PROVIDER-AGNOSTIC. Postgres on AWS RDS,
 * Azure Database for Postgres, and self-hosted Postgres all share the
 * same `postgres` semantic type — only `props.hostingProvider` differs.
 */

import { registerOntologyTypes } from '../../lib/graph/ontology';

/**
 * Concrete semantic types contributed by the network domain.
 * Each maps to its abstract parent in the universal hierarchy.
 */
export const NETWORK_ONTOLOGY_TYPES: Record<string, string> = {
  // ─── Relational databases ─────────────
  postgres: 'relational_database',
  mysql: 'relational_database',
  mariadb: 'relational_database',
  oracle: 'relational_database',
  sqlserver: 'relational_database',
  aurora_postgres: 'postgres',
  aurora_mysql: 'mysql',
  cockroachdb: 'relational_database',
  // Importer-key aliases for AWS RDS — the cloud walker emits these
  // provider-prefixed forms, so register them as siblings of the canonical
  // engine names to keep `isA(node.props.resourceType, 'database')` truthy.
  rds_postgres: 'postgres',
  rds_mysql: 'mysql',
  rds_mariadb: 'mariadb',
  rds_oracle: 'oracle',
  rds_sqlserver: 'sqlserver',

  // ─── Document databases ─────────────
  mongodb: 'document_database',
  dynamodb: 'document_database',
  cosmosdb: 'document_database',
  firestore: 'document_database',
  couchdb: 'document_database',
  // Importer-key alias — AWS DocumentDB walker emits this short form.
  documentdb: 'document_database',

  // ─── Key-value stores ─────────────
  redis: 'key_value_store',
  memcached: 'key_value_store',
  etcd: 'key_value_store',
  riak: 'key_value_store',
  // Importer-key aliases — AWS ElastiCache walker emits engine-suffixed forms.
  elasticache_redis: 'redis',
  elasticache_memcached: 'memcached',

  // ─── Wide column ─────────────
  cassandra: 'wide_column_store',
  bigtable: 'wide_column_store',
  hbase: 'wide_column_store',

  // ─── Graph databases ─────────────
  neo4j: 'graph_database',
  neptune: 'graph_database',

  // ─── Time series ─────────────
  influxdb: 'time_series_database',
  timestream: 'time_series_database',
  prometheus: 'time_series_database',

  // ─── Search ─────────────
  elasticsearch: 'search_index',
  opensearch: 'search_index',
  algolia: 'search_index',

  // ─── Vector ─────────────
  pinecone: 'vector_database',
  weaviate: 'vector_database',
  qdrant: 'vector_database',

  // ─── Data warehouse ─────────────
  redshift: 'data_warehouse',
  bigquery: 'data_warehouse',
  snowflake: 'data_warehouse',
  databricks: 'data_warehouse',

  // ─── Object storage (concrete products under the abstract type) ─────────────
  s3_bucket: 'object_storage',
  gcs_bucket: 'object_storage',
  azure_blob: 'object_storage',
  // Importer-key aliases — `inferEdges` / cloud walkers emit the short form;
  // register them as siblings so `isA('s3', 'datastore')` works at the
  // federation layer without forcing every importer to use the canonical name.
  s3: 'object_storage',
  gcs: 'object_storage',
  azure_blob_storage: 'object_storage',

  // ─── File / block (concrete products) ─────────────
  efs_volume: 'file_storage',
  fsx_volume: 'file_storage',
  azure_files: 'file_storage',
  ebs_volume: 'block_storage',
  azure_managed_disk: 'block_storage',
  gcp_persistent_disk: 'block_storage',
  // Importer-key aliases — AWS walkers emit the short forms.
  efs: 'file_storage',
  ebs: 'block_storage',

  // ─── Caches (alternative branch — sometimes treated separately) ─────────────
  // (redis/memcached registered above as key_value_store)

  // ─── Compute / server software stacks ─────────────
  // Generic "a server with software" — the type is the software, hosting on props
  nginx: 'server',
  apache: 'server',
  tomcat: 'server',
  nodejs_app: 'application',
  python_app: 'application',
  java_app: 'application',
  go_app: 'application',
  ruby_app: 'application',

  // ─── Container orchestration ─────────────
  kubernetes_cluster: 'container_orchestrator',
  ecs_cluster: 'container_orchestrator',
  nomad_cluster: 'container_orchestrator',

  // ─── Identity providers ─────────────
  cognito_user_pool: 'identity_provider',
  azure_ad: 'identity_provider',
  okta: 'identity_provider',
  auth0: 'identity_provider',

  // ─── Network-path leaves referenced by AWS_ICON_REGISTRY ─────────────
  // These are concrete sub-types that don't have richer abstractions yet.
  // Parents pulled from the universal `network_path` / `observability_artifact`
  // / `compute` buckets in `src/lib/graph/ontology.ts`.
  dns_record: 'dns_zone',
  route_table: 'network_path',
  load_balancer_target: 'load_balancer',
  monitoring_alarm: 'observability_artifact',
  autoscaling_group: 'compute',

  // ─── Basic / provider-neutral shape leaves ─────────────
  // `BasicShapeManifests` (basic-shapes.manifest.ts) declares these as
  // semantic types so `isA(basic_server.semanticType, 'server')` and
  // `isA(aws_ec2.semanticType, 'server')` both return true. Without these
  // registrations the basic shapes are orphaned from the IS-A graph and
  // cross-library agent queries silently miss them.
  laptop: 'compute',
  workstation: 'compute',
  router: 'network_edge',
  switch: 'network_edge',
  firewall: 'network_edge',
  storage: 'datastore',
  internet: 'network_boundary',
};

/**
 * Call once at network domain bootstrap to register all semantic types.
 */
export function registerNetworkOntology(): void {
  registerOntologyTypes(NETWORK_ONTOLOGY_TYPES);
}
